import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { Skeleton } from '@hungryroot/pho';
import { PDPModal } from '@hungryroot/rdp';
import {
  EDIT_DELIVERY_GROCERIES_ROUTE,
  EDIT_DELIVERY_RECIPES_ROUTE,
} from '@hungryroot/routes';
import {
  buttonClickedEvent,
  pairingInteraction,
  productInteraction,
} from '@hungryroot/segment';
import { Button, Heading, Paragraph } from '@hungryroot/ui-kit';

import { EditableFavoritableRdpModalFromPairingId } from '../../../../components/RDP/Modal/EditableFavoritableRdpModalFromPairingId';
import { useNextNavigation } from '../../../hooks/useNextNavigation';
import { ProductCard } from './components/ProductCard';
import { RecipeCard } from './components/RecipeCard';
import { SkeletonCard } from './components/SkeletonCard';
import { useHouseholdVarietyGroceries } from './hooks/useHouseholdVarietyGroceries';
import { useHouseholdVarietyRecipes } from './hooks/useHouseholdVarietyRecipes';
import styles from './HouseholdVarietyDeflection.module.css';

const PREPARED_MEALS_URL = `${EDIT_DELIVERY_GROCERIES_ROUTE}?is_ready_to_eat=1`;
const LOADING_SKELETON_COUNT = 9;

export function HouseholdVarietyDeflection() {
  const {
    proteinSection,
    dietSection,
    kidRecipes,
    isLoading: isRecipesLoading,
  } = useHouseholdVarietyRecipes();
  const {
    groceries,
    preparedMeals,
    isLoading: isGroceriesLoading,
  } = useHouseholdVarietyGroceries();
  const { navigateFromDeflectionContinue } = useNextNavigation();
  const history = useHistory();

  const [openRdpPairingId, setOpenRdpPairingId] = useState<number | null>(null);
  const [pdpProductId, setPdpProductId] = useState<number | null>(null);

  const isLoading = isRecipesLoading || isGroceriesLoading;

  const hasProteins = proteinSection.length > 0;
  const hasDiets = dietSection.length > 0;
  const hasKids = kidRecipes.length > 0;
  const hasGroceries = groceries.length > 0;
  const hasPrepared = preparedMeals.length > 0;
  const hasNothingToShow =
    !isLoading &&
    !hasProteins &&
    !hasDiets &&
    !hasKids &&
    !hasGroceries &&
    !hasPrepared;

  useEffect(() => {
    if (hasNothingToShow) {
      navigateFromDeflectionContinue({ replace: true });
    }
  }, [hasNothingToShow, navigateFromDeflectionContinue]);

  if (hasNothingToShow) {
    return null;
  }

  return (
    <div className={styles.container}>
      {isLoading ? (
        <>
          <Skeleton className={styles['skeleton-title']} />
          <Skeleton className={styles['skeleton-subcopy']} />
          <Skeleton className={styles['skeleton-subcopy']} />
          <div className={clsx(styles.grid, styles['grid-protein'])}>
            {Array.from({ length: LOADING_SKELETON_COUNT }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </>
      ) : (
        <>
          <Heading variant="Hero" className={styles.title}>
            Build a cart that works for everyone
          </Heading>
          <Paragraph className={styles.subtitle}>
            Customize your weekly box to fit every diet, every preference, and
            every member of your family — swap recipes, add groceries, and mix
            in prepared meals so no one is left out.
          </Paragraph>

          {hasProteins && (
            <section className={styles.section}>
              <Heading variant="H4" className={styles['section-heading']}>
                Variety of proteins
              </Heading>
              <div className={clsx(styles.grid, styles['grid-protein'])}>
                {proteinSection.map(({ key, label, recipe }) => (
                  <RecipeCard
                    key={key}
                    recipe={recipe}
                    badgeLabel={label}
                    onClick={() => {
                      pairingInteraction({
                        pairingId: recipe.id,
                        pairingName: recipe.name,
                        actionType: 'clicked',
                        location: 'card',
                      });
                      setOpenRdpPairingId(recipe.id);
                    }}
                  />
                ))}
              </div>
              <div className={styles['section-cta']}>
                <Button
                  onClick={() => {
                    buttonClickedEvent({
                      button_name: 'Shop all proteins',
                      order_id: null,
                      page_position: 'cancel_deflection_household_variety',
                    });
                    history.push(EDIT_DELIVERY_RECIPES_ROUTE);
                  }}
                  style="default"
                  size="medium"
                  prominence="secondary"
                  textPrimary="Shop all proteins"
                />
              </div>
            </section>
          )}

          {hasDiets && (
            <section className={styles.section}>
              <Heading variant="H4" className={styles['section-heading']}>
                Diets we support
              </Heading>
              <div className={styles.grid}>
                {dietSection.map(({ key, label, recipe }) => (
                  <RecipeCard
                    key={key}
                    recipe={recipe}
                    badgeLabel={label}
                    onClick={() => {
                      pairingInteraction({
                        pairingId: recipe.id,
                        pairingName: recipe.name,
                        actionType: 'clicked',
                        location: 'card',
                      });
                      setOpenRdpPairingId(recipe.id);
                    }}
                  />
                ))}
              </div>
              <div className={styles['section-cta']}>
                <Button
                  onClick={() => {
                    buttonClickedEvent({
                      button_name: 'Shop all diets',
                      order_id: null,
                      page_position: 'cancel_deflection_household_variety',
                    });
                    history.push(EDIT_DELIVERY_RECIPES_ROUTE);
                  }}
                  style="default"
                  size="medium"
                  prominence="secondary"
                  textPrimary="Shop all diets"
                />
              </div>
            </section>
          )}

          {hasKids && (
            <section className={styles.section}>
              <Heading variant="H4" className={styles['section-heading']}>
                Kid-approved favorites
              </Heading>
              <div className={styles.grid}>
                {kidRecipes.map((recipe) => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    onClick={() => {
                      pairingInteraction({
                        pairingId: recipe.id,
                        pairingName: recipe.name,
                        actionType: 'clicked',
                        location: 'card',
                      });
                      setOpenRdpPairingId(recipe.id);
                    }}
                  />
                ))}
              </div>
              <div className={styles['section-cta']}>
                <Button
                  onClick={() => {
                    buttonClickedEvent({
                      button_name: "Shop kid's recipes",
                      order_id: null,
                      page_position: 'cancel_deflection_household_variety',
                    });
                    history.push(EDIT_DELIVERY_RECIPES_ROUTE);
                  }}
                  style="default"
                  size="medium"
                  prominence="secondary"
                  textPrimary="Shop kid's recipes"
                />
              </div>
            </section>
          )}

          {hasGroceries && (
            <>
              <hr className={styles.divider} aria-hidden="true" />
              <section className={styles.section}>
                <Heading variant="H4" className={styles['section-heading']}>
                  Groceries the whole family can add
                </Heading>
                <div className={styles.grid}>
                  {groceries.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onClick={() => {
                        productInteraction({
                          productId: product.id,
                          productName: product.name,
                          actionType: 'clicked',
                          location: 'card',
                        });
                        setPdpProductId(product.id);
                      }}
                    />
                  ))}
                </div>
                <div className={styles['section-cta']}>
                  <Button
                    onClick={() => {
                      buttonClickedEvent({
                        button_name: 'Shop all groceries',
                        order_id: null,
                        page_position: 'cancel_deflection_household_variety',
                      });
                      history.push(EDIT_DELIVERY_GROCERIES_ROUTE);
                    }}
                    style="default"
                    size="medium"
                    prominence="secondary"
                    textPrimary="Shop all groceries"
                  />
                </div>
              </section>
            </>
          )}

          {hasPrepared && (
            <>
              <hr className={styles.divider} aria-hidden="true" />
              <section className={styles.section}>
                <Heading variant="H4" className={styles['section-heading']}>
                  Heat & eat, ready to eat
                </Heading>
                <div className={styles.grid}>
                  {preparedMeals.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onClick={() => {
                        productInteraction({
                          productId: product.id,
                          productName: product.name,
                          actionType: 'clicked',
                          location: 'card',
                        });
                        setPdpProductId(product.id);
                      }}
                    />
                  ))}
                </div>
                <div className={styles['section-cta']}>
                  <Button
                    onClick={() => {
                      buttonClickedEvent({
                        button_name: 'Shop all meals',
                        order_id: null,
                        page_position: 'cancel_deflection_household_variety',
                      });
                      history.push(PREPARED_MEALS_URL);
                    }}
                    style="default"
                    size="medium"
                    prominence="secondary"
                    textPrimary="Shop all meals"
                  />
                </div>
              </section>
            </>
          )}
        </>
      )}

      {openRdpPairingId !== null && (
        <EditableFavoritableRdpModalFromPairingId
          pairingId={openRdpPairingId}
          onDismiss={() => setOpenRdpPairingId(null)}
        />
      )}

      {pdpProductId !== null && (
        <PDPModal
          id={pdpProductId}
          onDismiss={() => setPdpProductId(null)}
          stacked={false}
          footerButton={<></>}
          enableFoodProfileNeverWarnings={true}
        />
      )}
    </div>
  );
}
